// Exercício físico e suplementação (Bem-Estar, 0.34).
import { useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import type { Exercicio, Suplemento, TipoTreino, Treino } from '../../store/types';
import { salvarSuplemento, salvarTreino, TODOS_DIAS } from '../../store/bemestar';
import { EXERCICIOS, SUPLEMENTOS, TIPOS_TREINO } from '../../lib/bemestarCatalogo';
import { Btn, Chip, Field, Sheet } from '../ui';
import { PickerModal } from '../Pickers';
import { EmojiPicker } from '../EmojiPicker';
import { Ordenavel, ScrollArrastavel, semArrastar } from '../Arrastar';
import { ItemDeslizar, excluirPequeno, inserirEm } from '../ItemDeslizar';
import { C, alpha } from '../theme';
import { hm, horaTela, uid } from '../../lib/date';
import { t } from '../../lib/i18n';
import { AlertasDaRotina, Dias, apagarBE, confirmarExcluirBE, diasLabel, horaDate } from './comum';
import { ROLAGEM_TECLADO } from '../teclado';

export const novoTreino = (tipo: TipoTreino): Treino => {
  const x = TIPOS_TREINO.find(y => y.id === tipo)!;
  return { id: uid(), nome: t(x.nome), icon: x.icon, tipo, hora: null, dias: [1, 3, 5], exercicios: [] };
};
export const pausaTxt = (s: number) => (s >= 60 ? (s % 60 ? `${Math.floor(s / 60)}min${s % 60}s` : t('{n} min', { n: s / 60 })) : `${s}s`);
const nomeEx = (e: Exercicio) => [e.nome, e.series && e.reps ? `${e.series}×${e.reps}` : '', e.carga ? `${e.carga} kg` : '', e.min ? t('{n} min', { n: e.min }) : '',
  e.pausa ? t('pausa {p}', { p: pausaTxt(e.pausa) }) : ''].filter(Boolean).join(' · ');
export const resumoTreino = (x: Treino) => x.exercicios.length ? x.exercicios.map(e => e.nome).join(', ') : t('Sem exercícios');
export { nomeEx };

function Num({ v, on, rot, cor }: { v?: number; on: (n?: number) => void; rot: string; cor: string }) {
  return (
    <View style={{ alignItems: 'center', gap: 2 }}>
      <Text style={{ color: C.sub, fontSize: 10 }}>{rot}</Text>
      <Field {...semArrastar} value={v ? String(v) : ''} onChangeText={s => { const n = Number(s.replace(',', '.')); on(isFinite(n) && n > 0 ? n : undefined); }}
        keyboardType="decimal-pad" style={{ width: 58, textAlign: 'center', paddingVertical: 8, borderWidth: 1, borderColor: alpha(cor, 0.4) }} />
    </View>
  );
}

export function TreinoSheet({ treino, cor, onClose }: { treino: Treino; cor: string; onClose: () => void }) {
  const [x, setX] = useState(treino);
  const [hora, setHora] = useState(false);
  const [emoji, setEmoji] = useState(false);
  const [txt, setTxt] = useState('');
  const salvar = () => {
    if (!x.nome.trim()) return Alert.alert(t('Dê um nome ao treino.'));
    salvarTreino({ ...x, nome: x.nome.trim(), exercicios: txt.trim() ? [...x.exercicios, { id: uid(), nome: txt.trim() }] : x.exercicios });
    onClose();
  };
  const tem = (n: string) => x.exercicios.some(e => e.nome === n);
  const alternar = (n: string, tempo: boolean) => setX(v => ({ ...v, exercicios: tem(n) ? v.exercicios.filter(e => e.nome !== n)
    : [...v.exercicios, { id: uid(), nome: n, ...(tempo ? { min: 30 } : { series: 3, reps: 12 }) }] }));
  const tirarEx = (e: Exercicio) => excluirPequeno(true, () => {
    const pos = x.exercicios.findIndex(y => y.id === e.id);
    setX(v => ({ ...v, exercicios: v.exercicios.filter(y => y.id !== e.id) }));
    return () => setX(v => (v.exercicios.some(y => y.id === e.id) ? v : { ...v, exercicios: inserirEm(v.exercicios, pos, e) }));
  });
  const mudar = (id: string, p: Partial<Exercicio>) => setX(v => ({ ...v, exercicios: v.exercicios.map(e => (e.id === id ? { ...e, ...p } : e)) }));
  return (
    <Sheet visible onClose={onClose} title={t('Exercício físico')} right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      <ScrollArrastavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
          <Pressable onPress={() => setEmoji(true)} style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: alpha(cor, 0.2), alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 28 }}>{x.icon}</Text></Pressable>
          <Field value={x.nome} placeholder={t('Nome (ex.: Treino A)')} style={{ flex: 1 }} onChangeText={nome => setX({ ...x, nome })} />
        </View>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {TIPOS_TREINO.map(tp => <Chip key={tp.id} cor={cor} on={x.tipo === tp.id} label={`${tp.icon} ${t(tp.nome)}`} onPress={() => setX({ ...x, tipo: tp.id })} />)}
        </View>
        <Pressable onPress={() => setHora(true)} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 14, padding: 14 }}>
          <Text style={{ color: C.sub, flex: 1 }}>🕐 {t('Horário')}</Text>
          <Text style={{ color: C.text, fontWeight: '700' }}>{x.hora ? horaTela(x.hora) : t('Sem horário')}</Text>
          {x.hora && <Pressable hitSlop={8} onPress={() => setX({ ...x, hora: null })}><Text style={{ color: C.mute, marginLeft: 10 }}>✕</Text></Pressable>}
        </Pressable>
        <AlertasDaRotina itens={x.alertas} hora={x.hora} cor={cor} onChange={alertas => setX(y => ({ ...y, alertas }))} />
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Dias: {dias}', { dias: diasLabel(x.dias) })}</Text>
        <Dias v={x.dias} cor={cor} on={dias => setX({ ...x, dias })} />

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('ESCOLHA AS ATIVIDADES')}</Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {EXERCICIOS[x.tipo].map(e => <Chip key={e.nome} small cor={cor} on={tem(t(e.nome))} label={(tem(t(e.nome)) ? '✓ ' : '') + t(e.nome)} onPress={() => alternar(t(e.nome), e.tipo === 'tempo')} />)}
        </View>

        {x.exercicios.length > 0 && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('SEU TREINO')}</Text>}
        {/* texto 26: sem ⠿ (segurar no cartão e arrastar); deslizar → exclui, ← duplica */}
        <Ordenavel itens={x.exercicios} chave={e => e.id} alca gap={6} onOrdem={l => setX({ ...x, exercicios: l })} render={(e, i, a) => (
          <ItemDeslizar onExcluir={() => tirarEx(e)} onDuplicar={() => setX({ ...x, exercicios: inserirEm(x.exercicios, i + 1, { ...e, id: uid() }) })}>
          <View {...a.alca} style={{ backgroundColor: C.card, borderRadius: 12, padding: 10, gap: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ color: cor, fontWeight: '800', width: 18 }}>{i + 1}</Text>
              <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{e.nome}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 6, marginLeft: 44, flexWrap: 'wrap' }}>
              <Num rot={t('Séries')} v={e.series} cor={cor} on={n => mudar(e.id, { series: n })} />
              <Num rot={t('Repetições')} v={e.reps} cor={cor} on={n => mudar(e.id, { reps: n })} />
              <Num rot={t('Carga (kg)')} v={e.carga} cor={cor} on={n => mudar(e.id, { carga: n })} />
              <Num rot={t('Minutos')} v={e.min} cor={cor} on={n => mudar(e.id, { min: n })} />
              <Num rot={t('Pausa (s)')} v={e.pausa} cor={cor} on={n => mudar(e.id, { pausa: n })} />
            </View>
            <View style={{ flexDirection: 'row', gap: 6, marginLeft: 44, flexWrap: 'wrap' }}>
              {[30, 60, 90, 120].map(sg => <Chip key={sg} small cor={cor} on={e.pausa === sg} label={'⏸ ' + pausaTxt(sg)} onPress={() => mudar(e.id, { pausa: e.pausa === sg ? undefined : sg })} />)}
            </View>
          </View>
          </ItemDeslizar>
        )} />
        <Field value={txt} placeholder={t('Outra atividade (digite e dê Enter)')} onChangeText={setTxt} blurOnSubmit={false}
          onSubmitEditing={() => { if (txt.trim()) { setX({ ...x, exercicios: [...x.exercicios, { id: uid(), nome: txt.trim() }] }); setTxt(''); } }} />
        <Btn cor={cor} label={t('Salvar treino')} onPress={salvar} />
        {treino.exercicios.length > 0 && (
          <Pressable style={{ padding: 10 }} onPress={() => confirmarExcluirBE(x.nome, () => { apagarBE('treinos', x.id); onClose(); })}>
            <Text style={{ color: C.danger, textAlign: 'center' }}>{t('Excluir treino')}</Text></Pressable>
        )}
      </ScrollArrastavel>
      {hora && <PickerModal mode="time" value={horaDate(x.hora)} cor={cor} onClose={() => setHora(false)} onPick={v => setX(y => ({ ...y, hora: hm(v) }))} />}
      <EmojiPicker visible={emoji} cor={cor} onClose={() => setEmoji(false)} onPick={e => setX(y => ({ ...y, icon: e }))} />
    </Sheet>
  );
}

export const novoSuplemento = (p?: (typeof SUPLEMENTOS)[number]): Suplemento =>
  ({ id: uid(), nome: p ? t(p.nome) : '', icon: p?.icon ?? '💊', dose: p ? t(p.dose) : '', hora: '08:00', dias: TODOS_DIAS });

export function SuplementoSheet({ sup, cor, onClose }: { sup: Suplemento; cor: string; onClose: () => void }) {
  const [x, setX] = useState(sup);
  const [hora, setHora] = useState(false);
  const [emoji, setEmoji] = useState(false);
  const todo = x.dias.length === 7;
  const salvar = () => {
    if (!x.nome.trim()) return Alert.alert(t('Dê um nome ao suplemento.'));
    if (!x.dias.length) return Alert.alert(t('Escolha pelo menos um dia.'));
    salvarSuplemento({ ...x, nome: x.nome.trim(), dose: x.dose.trim() }); onClose();
  };
  return (
    <Sheet visible onClose={onClose} title={t('Suplemento')} right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
          {SUPLEMENTOS.map(p => <Chip key={p.nome} small cor={cor} on={x.nome === t(p.nome)} label={`${p.icon} ${t(p.nome)}`}
            onPress={() => setX({ ...x, nome: t(p.nome), icon: p.icon, dose: x.dose || t(p.dose) })} />)}
        </View>
        <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
          <Pressable onPress={() => setEmoji(true)} style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: alpha(cor, 0.2), alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 28 }}>{x.icon}</Text></Pressable>
          <Field value={x.nome} placeholder={t('Nome (ex.: Vitamina C)')} style={{ flex: 1 }} onChangeText={nome => setX({ ...x, nome })} />
        </View>
        <Field value={x.dose} placeholder={t('Dose (ex.: 1 cápsula, 5 g)')} onChangeText={dose => setX({ ...x, dose })} />
        <Pressable onPress={() => setHora(true)} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 14, padding: 14 }}>
          <Text style={{ color: C.sub, flex: 1 }}>🕐 {t('Horário')}</Text>
          <Text style={{ color: C.text, fontWeight: '700' }}>{x.hora ? horaTela(x.hora) : t('Sem horário')}</Text>
          {x.hora && <Pressable hitSlop={8} onPress={() => setX({ ...x, hora: null })}><Text style={{ color: C.mute, marginLeft: 10 }}>✕</Text></Pressable>}
        </Pressable>
        <AlertasDaRotina itens={x.alertas} hora={x.hora} cor={cor} onChange={alertas => setX(y => ({ ...y, alertas }))} />
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Frequência')}</Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip cor={cor} on={todo} label={t('Todo dia')} onPress={() => setX({ ...x, dias: TODOS_DIAS })} />
          <Chip cor={cor} on={!todo} label={t('Dias específicos')} onPress={() => setX({ ...x, dias: todo ? [1] : x.dias })} />
        </View>
        {!todo && <Dias v={x.dias} cor={cor} on={dias => setX({ ...x, dias })} />}
        <Btn cor={cor} label={t('Salvar suplemento')} onPress={salvar} />
        {!!sup.nome && (
          <Pressable style={{ padding: 10 }} onPress={() => confirmarExcluirBE(x.nome, () => { apagarBE('suplementos', x.id); onClose(); })}>
            <Text style={{ color: C.danger, textAlign: 'center' }}>{t('Excluir suplemento')}</Text></Pressable>
        )}
      </ScrollView>
      {hora && <PickerModal mode="time" value={horaDate(x.hora)} cor={cor} onClose={() => setHora(false)} onPick={v => setX(y => ({ ...y, hora: hm(v) }))} />}
      <EmojiPicker visible={emoji} cor={cor} onClose={() => setEmoji(false)} onPick={e => setX(y => ({ ...y, icon: e }))} />
    </Sheet>
  );
}
